
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, Building2, Layout, CheckSquare, AlertTriangle, Boxes, Puzzle, BadgeDollarSign } from "lucide-react";
import { AdminDashboardMetrics as AdminMetrics } from "@/types/admin";

interface DashboardMetric {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: string | number;
  description?: string;
}

export const AdminDashboardMetrics = () => {
  const { data: metrics, isLoading, error } = useQuery({
    queryKey: ['adminMetrics'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_admin_dashboard_metrics');
      if (error) {
        console.error('Error fetching metrics:', error);
        throw error;
      }
      // Type assertion to ensure the data matches our AdminMetrics type
      return data as unknown as AdminMetrics;
    }
  });

  console.log('Admin Metrics:', metrics); // Para debugging

  if (error) {
    console.error('Error en métricas:', error);
    return (
      <div className="p-4 text-red-500">
        Error al cargar las métricas del dashboard
      </div>
    );
  }

  const dashboardMetrics: DashboardMetric[] = [
    {
      icon: Users,
      title: "Total Usuarios",
      value: metrics?.total_users ?? 0,
      description: `${metrics?.active_users ?? 0} usuarios activos`
    },
    {
      icon: Building2,
      title: "Organizaciones",
      value: metrics?.total_organizations ?? 0
    },
    {
      icon: Layout,
      title: "Workspaces",
      value: metrics?.total_workspaces ?? 0
    },
    {
      icon: Boxes,
      title: "Boards",
      value: metrics?.total_boards ?? 0
    },
    {
      icon: CheckSquare,
      title: "Tickets Pendientes",
      value: metrics?.pending_tickets ?? 0
    },
    {
      icon: AlertTriangle,
      title: "Requieren Atención",
      value: metrics?.require_attention ?? 0,
      description: "En los próximos 7 días"
    },
    {
      icon: Puzzle,
      title: "Integraciones",
      value: metrics?.active_integrations ?? 0,
      description: `${metrics?.total_integrations ?? 0} integraciones totales`
    },
    {
      icon: BadgeDollarSign,
      title: "Plan Más Popular",
      value: metrics?.popular_plan ?? "Sin datos",
      description: `${metrics?.active_subscribers ?? 0} suscriptores activos`
    }
  ];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 min-h-[200px]">
        {Array.from({ length: 8 }).map((_, i) => (
          <Card key={i} className="p-6 bg-white dark:bg-gray-800">
            <Skeleton className="h-8 w-8 rounded-full" />
            <Skeleton className="h-4 w-24 mt-4" />
            <Skeleton className="h-8 w-16 mt-2" />
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4">
      {dashboardMetrics.map((metric, index) => (
        <Card key={index} className="p-6 bg-white dark:bg-gray-800">
          <div className="flex items-center space-x-4">
            <metric.icon className="h-8 w-8 text-primary" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{metric.title}</h3>
          </div>
          <div className="space-y-1 mt-4">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{metric.value}</p>
            {metric.description && (
              <p className="text-sm text-gray-500 dark:text-gray-400">{metric.description}</p>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
};
